import * as _ from 'lodash-es';
import type { Reducer } from 'react';
import { useReducer } from 'react';
import {
  MAGNETIC_WALL_COLLAPSED_WIDTH,
  MAGNETIC_WALL_MIN_WIDTH,
  PRIORITY_LIST_MAX_WIDTH,
  SIDEBAR_SHIFT_BREAKPOINT,
  SIDEBAR_WIDTH,
  STATIC_AREAS_WIDTH,
  TOC_COLLAPSED_WIDTH,
  TOC_MIN_WIDTH,
} from './PostLayoutSizes';
import exhaustiveMatchGuard from '../utils/exhaustiveMatchGuard';

type Area = 'sidebar' | 'magneticWall' | 'toc';
export type PriorityList = [Area, Area, Area] | [Area, Area] | [Area] | [];

/**
 * undefined - initial state. After leaving this state, layout will be automatically updated.
 * true - area can be expanded. It won't expand automatically when switching from `false` to avoid layout changes.
 * false - area can't be expanded. It won't collapse automatically when switching from `true` to avoid layout changes.
 */
export type ExpandableAreas = Record<Area, boolean | undefined>;

const DEFAULT_PRIORITY_LIST: PriorityList = ['sidebar', 'toc', 'magneticWall'];

interface PostLayoutState {
  expandedAreas: Set<Area>;
  expandableAreas: ExpandableAreas;
  priorityList: PriorityList;
  initialPriorityList: PriorityList;
  lastWidth: number;
}

// eslint-disable-next-line import/no-unused-modules -- expose for testing
export const INITIAL_STATE: PostLayoutState = {
  expandedAreas: new Set<Area>(),
  priorityList: DEFAULT_PRIORITY_LIST,
  initialPriorityList: DEFAULT_PRIORITY_LIST,
  lastWidth: 0,
  expandableAreas: { sidebar: true, toc: undefined, magneticWall: undefined },
};

type PostLayoutAction =
  | {
      type: 'TOGGLE_AREA';
      payload: {
        area: Area;
        isExpanded: boolean;
      };
    }
  | {
      type: 'RESIZE';
      payload: number;
    }
  | {
      type: 'SET_EXPANDABLE_AREAS';
      payload: ExpandableAreas;
    };

const prioritizeArea = (area: Area, priorityList: PriorityList) =>
  _.uniq([area, ...priorityList]) as PriorityList;

const deprioritizeArea = (area: Area, priorityList: PriorityList) =>
  _.without(priorityList, area) as PriorityList;

const expandArea = (area: Area, expandedAreas: Set<Area>) => {
  const newSet = new Set(expandedAreas);
  newSet.add(area);
  return newSet;
};

const collapseArea = (area: Area, expandedAreas: Set<Area>) => {
  const newSet = new Set(expandedAreas);
  newSet.delete(area);
  return newSet;
};

const computeExpectedWidthIfAreaIsExpanded = ({
  area,
  expandedAreas,
}: {
  area: Area;
  expandedAreas?: Set<Area>;
}) => {
  const areasWidth: Record<Area, [number, number]> = {
    sidebar: [0, SIDEBAR_WIDTH],
    toc: [TOC_COLLAPSED_WIDTH, TOC_MIN_WIDTH],
    magneticWall: [MAGNETIC_WALL_COLLAPSED_WIDTH, MAGNETIC_WALL_MIN_WIDTH],
  };

  const allAreas = new Set(expandedAreas).add(area);
  let result = STATIC_AREAS_WIDTH;
  for (const [currentArea, [collapsedWith, minWidth]] of Object.entries(
    areasWidth,
  )) {
    result += allAreas.has(currentArea as Area) ? minWidth : collapsedWith;
  }
  return result;
};

const canAreaExpand = ({
  area,
  currentWidth,
  expandableAreas,
  nextExpandedAreas,
}: {
  area: Area;
  currentWidth: number | null;
  expandableAreas: ExpandableAreas;
  nextExpandedAreas?: Set<Area>;
}) => {
  if (currentWidth == null) return false;

  const widthIfExpanded = computeExpectedWidthIfAreaIsExpanded({
    area,
    expandedAreas: nextExpandedAreas,
  });

  if (widthIfExpanded > currentWidth) return false;

  return !!expandableAreas[area];
};

const recalculateState = ({
  currentWidth,
  state,
}: {
  currentWidth: number | null;
  state: PostLayoutState;
}): PostLayoutState => {
  if (!currentWidth) return state;

  const {
    lastWidth,
    initialPriorityList,
    priorityList,
    expandableAreas,
    expandedAreas,
  } = state;

  const shouldOpenSidebar =
    lastWidth != null &&
    lastWidth < SIDEBAR_SHIFT_BREAKPOINT &&
    currentWidth >= SIDEBAR_SHIFT_BREAKPOINT;

  const shouldResetPriorityList =
    lastWidth != null &&
    lastWidth < PRIORITY_LIST_MAX_WIDTH &&
    currentWidth >= PRIORITY_LIST_MAX_WIDTH;

  const nextPriorityList = (() => {
    if (shouldResetPriorityList) {
      return initialPriorityList;
    }

    if (shouldOpenSidebar) {
      return prioritizeArea('sidebar', priorityList);
    }

    return priorityList;
  })();

  const result = nextPriorityList.reduce(
    (acc, area) => {
      const isAreaExpandable = canAreaExpand({
        area,
        currentWidth,
        expandableAreas,
        nextExpandedAreas: acc.expandedAreas,
      });

      if (area === 'sidebar') {
        const shouldExpand =
          isAreaExpandable &&
          (shouldOpenSidebar || expandedAreas.has('sidebar'));

        return {
          expandedAreas: shouldExpand
            ? expandArea('sidebar', acc.expandedAreas)
            : collapseArea('sidebar', acc.expandedAreas),
        };
      }

      if (area === 'toc') {
        return {
          expandedAreas: isAreaExpandable
            ? expandArea('toc', acc.expandedAreas)
            : collapseArea('toc', acc.expandedAreas),
        };
      }

      if (area === 'magneticWall') {
        return {
          expandedAreas: isAreaExpandable
            ? expandArea('magneticWall', acc.expandedAreas)
            : collapseArea('magneticWall', acc.expandedAreas),
        };
      }
      return acc;
    },
    { expandedAreas: new Set<Area>([]) },
  );

  return {
    ...state,
    priorityList: nextPriorityList,
    expandedAreas: result.expandedAreas,
  };
};

// eslint-disable-next-line import/no-unused-modules -- expose for testing
export const reducer: Reducer<PostLayoutState, PostLayoutAction> = (
  state: PostLayoutState,
  action: PostLayoutAction,
) => {
  switch (action.type) {
    case 'SET_EXPANDABLE_AREAS': {
      const allowedAreas = action.payload;
      if (_.isEqual(state.expandableAreas, allowedAreas)) return state;

      const allAreas = Object.keys(state.expandableAreas) as Area[];
      const isInitializingAreaState = allAreas.some(
        (area) =>
          state.expandableAreas[area] === undefined &&
          state.expandableAreas[area] !== allowedAreas[area],
      );

      if (isInitializingAreaState) {
        return recalculateState({
          currentWidth: state.lastWidth,
          state: {
            ...state,
            expandableAreas: allowedAreas,
          },
        });
      }

      return {
        ...state,
        expandableAreas: allowedAreas,
      };
    }
    case 'TOGGLE_AREA': {
      const { area, isExpanded } = action.payload;

      if (isExpanded === state.expandedAreas.has(area)) {
        return state;
      }

      if (
        !canAreaExpand({
          area,
          currentWidth: state.lastWidth,
          expandableAreas: state.expandableAreas,
        })
      ) {
        return state;
      }

      const expandedAreas = isExpanded
        ? expandArea(area, state.expandedAreas)
        : collapseArea(area, state.expandedAreas);

      // user action, therefore priority changes
      const priorityList = isExpanded
        ? prioritizeArea(area, state.priorityList)
        : deprioritizeArea(area, state.priorityList);

      const result = recalculateState({
        currentWidth: state.lastWidth,
        state: {
          ...state,
          priorityList,
          expandedAreas,
        },
      });

      return {
        ...state,
        ...result,
      };
    }
    case 'RESIZE': {
      const currentWidth = action.payload;

      return {
        ...recalculateState({
          currentWidth,
          state,
        }),
        lastWidth: action.payload,
      };
    }
    default: {
      exhaustiveMatchGuard(action);
      return state;
    }
  }
};

const usePostLayoutStateReducer = ({
  isSidebarOpenInitially,
  expandableAreas,
  initialPriorityList = DEFAULT_PRIORITY_LIST,
  getInitialWidth,
}: {
  isSidebarOpenInitially: boolean;
  expandableAreas: ExpandableAreas;
  initialPriorityList?: PriorityList;
  getInitialWidth: () => number;
}) => {
  const [state, dispatch] = useReducer(
    reducer,
    {
      ...INITIAL_STATE,
      initialPriorityList,
      priorityList: initialPriorityList,
      expandableAreas,
      expandedAreas: isSidebarOpenInitially
        ? new Set(['sidebar' as const])
        : new Set([]),
    },
    (initialState) => {
      return recalculateState({
        currentWidth: getInitialWidth(),
        state: {
          ...initialState,
          lastWidth: getInitialWidth(),
        },
      });
    },
  );

  if (!_.isEqual(state.expandableAreas, expandableAreas)) {
    dispatch({
      type: 'SET_EXPANDABLE_AREAS',
      payload: expandableAreas,
    });
  }

  return {
    state,
    dispatch,
    canMagneticWallExpand: canAreaExpand({
      area: 'magneticWall',
      currentWidth: state.lastWidth,
      expandableAreas: state.expandableAreas,
    }),
    canTOCExpand: canAreaExpand({
      area: 'toc',
      currentWidth: state.lastWidth,
      expandableAreas: state.expandableAreas,
    }),
  };
};

export default usePostLayoutStateReducer;
